import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// ✅ Register once at module level
gsap.registerPlugin(ScrollTrigger);

// ✅ Stable references outside component to avoid recreation
const row1Items = ['Digital Marketing', 'E-commerce', 'Web Development', 'App Development'];
const row2Items = ['SEO Engineering', 'Graphic Designing', 'eBay Mastery', 'Amazon FBA'];
const row3Items = ['UI/UX Perfection', 'WordPress Dev', 'Brand Aesthetics', 'Scalable Architecture'];

// Pre-duplicate at module level
const makeRow = (items) => [...items, ...items, ...items, ...items];
const rows = [makeRow(row1Items), makeRow(row2Items), makeRow(row3Items)];

const MarqueeWall = () => {
  const containerRef = useRef(null);
  const line1Ref = useRef(null);
  const line2Ref = useRef(null);
  const line3Ref = useRef(null);
  const lineRefs = [line1Ref, line2Ref, line3Ref];
  const directions = [1, -1, 1]; // positive = left-to-right

  useEffect(() => {
    let ctx;
    // ✅ FIX: Wrap setup in requestAnimationFrame to prevent immediate layout reads (.scrollWidth)
    // from causing forced reflow / CPU spikes on mobile mount.
    const rafId = requestAnimationFrame(() => {
      ctx = gsap.context(() => {
        lineRefs.forEach((ref, i) => {
          const el = ref.current;
          if (!el) return;

          // Read scrollWidth safely after layout settlement frame
          const totalWidth = el.scrollWidth / 4; 
          const dir = directions[i];

          gsap.to(el, {
            x: dir > 0 ? `+=${totalWidth}` : `-=${totalWidth}`,
            duration: 40,
            ease: 'none',
            repeat: -1,
            modifiers: {
              x: gsap.utils.unitize((x) => {
                const px = parseFloat(x);
                return dir > 0
                  ? (px % totalWidth) - totalWidth
                  : px % totalWidth;
              }),
            },
          });
        });

        // ✅ Scroll-driven displacement using fastScrollEnd for smooth mobile scroll
        const scrollAnim = gsap.timeline({
          scrollTrigger: {
            trigger: containerRef.current,
            start: 'top bottom',
            end: 'bottom top',
            scrub: 2.5,
            fastScrollEnd: true,
          },
        });

        scrollAnim
          .to(line1Ref.current, { xPercent: 3 }, 0)
          .to(line2Ref.current, { xPercent: -3 }, 0)
          .to(line3Ref.current, { xPercent: 3 }, 0);

      }, containerRef);
    });

    return () => {
      cancelAnimationFrame(rafId);
      if (ctx) ctx.revert(); // Clean up all tweens & ScrollTriggers safely
    };
  }, []);

  return (
    <div ref={containerRef} style={styles.wrapperCanvas}>
      {[line1Ref, line2Ref, line3Ref].map((ref, rowIndex) => (
        <div key={rowIndex} style={styles.marqueeRowContainer}>
          <div ref={ref} style={styles.marqueeTrack}>
            {rows[rowIndex].map((item, idx) => (
              <div key={idx} style={styles.marqueeNode}>
                <span style={styles.textElement}>{item}</span>
                <span style={styles.orangeX} aria-hidden="true">×</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const styles = {
  wrapperCanvas: {
    backgroundColor: '#ffffff',
    padding: '100px 0',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    gap: '35px',
    width: '100%',
  },
  marqueeRowContainer: {
    display: 'flex',
    width: '100%',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  marqueeTrack: {
    display: 'inline-flex',
    alignItems: 'center',
    willChange: 'transform',
    // ✅ Add backfaceVisibility to force hardware acceleration layer on mobile GPUs
    backfaceVisibility: 'hidden',
    WebkitBackfaceVisibility: 'hidden',
  },
  marqueeNode: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0 20px',
  },
  textElement: {
    fontFamily: 'epilogue, sans-serif',
    fontSize: 'clamp(1rem, 2vw, 2.3rem)',
    fontWeight: '650',
    color: '#111827',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  orangeX: {
    color: '#F97316',
    marginLeft: '80px',
    fontSize: '2.2rem',
    fontWeight: '400',
    userSelect: 'none',
    opacity: 0.85,
  },
};

export default MarqueeWall;